/**
 * SCR-034 — Account settings (shared across all roles).
 * beforeLoad: checks login only (no role check).
 * Two independent forms: profile info + change password.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@client/lib/auth-client';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/account')({
  beforeLoad: async ({ navigate }) => {
    const { data: session } = await authClient.getSession();
    if (!session?.user) {
      throw navigate({ to: '/login', search: { redirect: '/account' }, replace: true });
    }
  },
  component: AccountPage,
});

interface UserProfile {
  fullName: string;
  phone?: string | null;
  email: string;
  role: string;
  branchName?: string | null;
  code?: string | null;
}

const roleLabels: Record<string, string> = {
  admin: 'Quản trị viên',
  manager: 'Quản lý',
  teacher: 'Giáo viên',
  student: 'Học viên',
  parent: 'Phụ huynh',
};

// Profile form schema
const profileSchema = z.object({
  fullName: z.string().min(2, 'Họ tên tối thiểu 2 ký tự').max(100),
  phone: z
    .string()
    .max(20)
    .regex(/^[\d\s+\-()]*$/, 'Số điện thoại không hợp lệ')
    .optional()
    .or(z.literal('')),
});
type ProfileForm = z.infer<typeof profileSchema>;

// Password form schema
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Vui lòng nhập mật khẩu hiện tại'),
    newPassword: z.string().min(8, 'Mật khẩu mới tối thiểu 8 ký tự'),
    confirmPassword: z.string().min(1, 'Vui lòng xác nhận mật khẩu'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Xác nhận mật khẩu không khớp',
    path: ['confirmPassword'],
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'Mật khẩu mới phải khác mật khẩu hiện tại',
    path: ['newPassword'],
  });
type PasswordForm = z.infer<typeof passwordSchema>;

function AccountPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Show/hide password toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { fullName: '', phone: '' },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  // Load profile on mount
  useEffect(() => {
    api.get<UserProfile>('/api/account/profile')
      .then((data) => {
        setProfile(data);
        profileForm.reset({
          fullName: data.fullName,
          phone: data.phone ?? '',
        });
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSaveProfile(values: ProfileForm) {
    setProfileError(null);
    try {
      const updated = await api.patch<UserProfile>('/api/account/profile', {
        fullName: values.fullName.trim(),
        phone: values.phone?.trim() || null,
      });
      setProfile(updated);
      profileForm.reset({ fullName: updated.fullName, phone: updated.phone ?? '' });
      toast.success('Đã cập nhật thông tin.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Không kết nối được máy chủ, thử lại sau.';
      setProfileError(msg);
    }
  }

  async function onChangePassword(values: PasswordForm) {
    setPasswordError(null);
    try {
      await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        revokeOtherSessions: false,
      });
      passwordForm.reset();
      toast.success('Đã đổi mật khẩu thành công.');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        setPasswordError('Mật khẩu hiện tại không đúng.');
      } else if (status === 429) {
        setPasswordError('Vui lòng thử lại sau.');
      } else {
        setPasswordError('Không kết nối được máy chủ, thử lại sau.');
      }
    }
  }

  function handleLogout() {
    authClient.signOut().then(() => void navigate({ to: '/login' }));
  }

  if (loading) return <AccountSkeleton />;

  if (loadError) {
    return (
      <div className="min-h-screen flex items-start justify-center pt-16 px-4">
        <Alert variant="destructive" className="max-w-lg w-full">
          <AlertDescription>
            Không tải được hồ sơ. Vui lòng liên hệ trung tâm.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const isDirty = profileForm.formState.isDirty;

  return (
    <div className="min-h-screen bg-background px-4 py-6 md:py-10">
      <div className="mx-auto w-full max-w-2xl flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-foreground">Cài đặt tài khoản</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            Đăng xuất
          </Button>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Thông tin tài khoản</CardTitle>
          </CardHeader>
          <CardContent>
            {profileError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{profileError}</AlertDescription>
              </Alert>
            )}
            <form
              onSubmit={profileForm.handleSubmit(onSaveProfile)}
              noValidate
              className="flex flex-col gap-4"
            >
              {/* Editable fields */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fullName" className="text-sm font-medium">Họ tên</Label>
                <Input
                  id="fullName"
                  className="h-11"
                  {...profileForm.register('fullName')}
                />
                {profileForm.formState.errors.fullName && (
                  <p className="text-xs text-destructive">
                    {profileForm.formState.errors.fullName.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="phone" className="text-sm font-medium">Số điện thoại</Label>
                <Input
                  id="phone"
                  type="tel"
                  className="h-11"
                  placeholder="Tùy chọn"
                  {...profileForm.register('phone')}
                />
                {profileForm.formState.errors.phone && (
                  <p className="text-xs text-destructive">
                    {profileForm.formState.errors.phone.message}
                  </p>
                )}
              </div>

              {/* Read-only fields */}
              <ReadOnlyField label="Email" value={profile?.email ?? ''} />
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-muted-foreground">Vai trò</span>
                <div className="flex items-center h-11 px-3 rounded-md border border-border bg-muted/50">
                  <Badge variant="secondary">
                    {roleLabels[profile?.role ?? ''] ?? profile?.role}
                  </Badge>
                </div>
              </div>
              {profile?.branchName && (
                <ReadOnlyField label="Cơ sở" value={profile.branchName} />
              )}
              {profile?.code && (
                <ReadOnlyField label="Mã" value={profile.code} mono />
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end mt-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11"
                  disabled={!isDirty || profileForm.formState.isSubmitting}
                  onClick={() =>
                    profileForm.reset({
                      fullName: profile?.fullName ?? '',
                      phone: profile?.phone ?? '',
                    })
                  }
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  className="h-11"
                  disabled={!isDirty || profileForm.formState.isSubmitting}
                >
                  {profileForm.formState.isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Đang lưu…
                    </span>
                  ) : (
                    'Lưu thay đổi'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Đổi mật khẩu</CardTitle>
          </CardHeader>
          <CardContent>
            {passwordError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            <form
              onSubmit={passwordForm.handleSubmit(onChangePassword)}
              noValidate
              className="flex flex-col gap-4"
            >
              <PasswordField
                id="currentPassword"
                label="Mật khẩu hiện tại"
                show={showCurrent}
                onToggle={() => setShowCurrent((v) => !v)}
                registration={passwordForm.register('currentPassword')}
                error={passwordForm.formState.errors.currentPassword?.message}
              />
              <PasswordField
                id="newPassword"
                label="Mật khẩu mới"
                hint="Tối thiểu 8 ký tự"
                show={showNew}
                onToggle={() => setShowNew((v) => !v)}
                registration={passwordForm.register('newPassword')}
                error={passwordForm.formState.errors.newPassword?.message}
              />
              <PasswordField
                id="confirmPassword"
                label="Xác nhận mật khẩu"
                show={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
                registration={passwordForm.register('confirmPassword')}
                error={passwordForm.formState.errors.confirmPassword?.message}
              />
              <Button
                type="submit"
                className="h-11 w-full sm:w-auto sm:self-end mt-2"
                disabled={passwordForm.formState.isSubmitting}
              >
                {passwordForm.formState.isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Đang đổi…
                  </span>
                ) : (
                  'Đổi mật khẩu'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <div
        className={`flex items-center h-11 px-3 rounded-md border border-border bg-muted/50 text-sm ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  hint,
  show,
  onToggle,
  registration,
  error,
}: {
  id: string;
  label: string;
  hint?: string;
  show: boolean;
  onToggle: () => void;
  registration: ReturnType<ReturnType<typeof useForm<PasswordForm>>['register']>;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          className="h-11 pr-10"
          {...registration}
        />
        <button
          type="button"
          aria-label={show ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          onClick={onToggle}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function AccountSkeleton() {
  return (
    <div className="min-h-screen bg-background px-4 py-6 md:py-10">
      <div className="mx-auto w-full max-w-2xl flex flex-col gap-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="flex flex-col gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
          <CardContent className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
